import { useContext, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'libs/router'
import { useForm } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { ITag } from 'interfaces/tag.interface'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { ProductSalesTable } from '../../organisms/Sales/Sales'
import { Header, Form, HistoryButton, BackButton, Button } from 'shared/components'
import { useEditProductValidationSchema } from './hooks/useEditProductValidationSchema'
import { useFetchProduct } from './queries/useFetchProduct'
import { usePopulateProduct } from './hooks/usePopulateProduct'
import { useSetColor } from './hooks/useSetColor'
import { useEditProductMutation } from './mutations/useEditProductMutation'
import { defaultConfig } from 'config/default'
import { routes } from 'config/routes'
import { useConfirmProductMutation } from './mutations/useConfirmProductMutation'
import { useProductOptions } from 'features/products/hooks/useProductOptions'
import { ProductFormSections } from 'features/products/organisms/ProductForm/ProductFormSections'
import { useProductFieldGates } from 'features/products/hooks/useProductFieldGates'

const outlineButton =
	'h-9 rounded-md border border-border bg-background px-4 text-sm font-medium text-foreground shadow hover:bg-muted'

const ProductPage = () => {
	const { t } = useTranslation()
	const params = useParams()
	const storeCtx = useContext(StoreSettingsContext)
	const validationResolver = useEditProductValidationSchema()
	const [searchParams] = useSearchParams()

	const { product } = useFetchProduct()
	const form = useForm({
		resolver: validationResolver,
		mode: 'onChange'
	})
	const { handleSubmit, setValue, reset, watch, setError } = form
	const gates = useProductFieldGates({ form, product: product?.data })

	const hasImage = !!watch('image')

	usePopulateProduct({
		data: product?.data,
		reset,
		setValue
	})

	const options = useProductOptions({
		bookingsEndpoint: '/bookings/',
		bookingsQuery: `end_date__gt=${new Date().toISOString().split('T')[0]}&is_active=true`
	})
	const { items: loadedTags, hasNextPage, isFetchingNextPage, fetchNextPage } = options.tags

	useEffect(() => {
		if (!product?.data?.tags?.length || !loadedTags.length) return

		const loadedNames = new Set(loadedTags.map((tag) => tag.name))
		const allFound = product.data.tags.every((name) => loadedNames.has(name))

		if (!allFound && hasNextPage && !isFetchingNextPage) {
			fetchNextPage()
		}
	}, [product?.data?.tags, loadedTags, hasNextPage, isFetchingNextPage, fetchNextPage])

	useEffect(() => {
		if (!product?.data?.tags?.length) return

		const tagsMap = new Map(loadedTags.map((tag) => [tag.name, tag]))
		const formTags = product.data.tags
			.map((name) => {
				const tag = tagsMap.get(name)

				return tag ? { value: tag.id, label: tag.name } : null
			})
			.filter(Boolean)

		if (formTags.length === product.data.tags.length) {
			setValue('tags', formTags)
		}
	}, [product?.data?.tags, loadedTags, setValue])

	const [productColor, setProductColor] = useState(defaultConfig.defaultProductColor)

	useSetColor({
		color: product?.data?.color,
		setProductColor
	})

	const handleProductColorChange = (data: { hex: string }) => setProductColor(data.hex)

	const { editProductMutation, isEditProductMutationLoading } = useEditProductMutation(setError)
	const {
		confirmProductMutation,
		unconfirmProductMutation,
		isConfirmProductMutationLoading,
		isUnconfirmProductMutationLoading
	} = useConfirmProductMutation()

	const onSubmit = (data: any) => {
		const formattedTags =
			data.tags != null && Array.isArray(data.tags) ? data.tags?.map((tag: ITag) => tag.label) : []
		const image = data.image ?? []

		const formData = new FormData()
		formData.append('name', data.name)
		formData.append('cost_price', data.cost_price)
		formData.append('price.amount', data.price)
		formData.append('booking_id', data.booking.value)
		if (storeCtx?.storeSettings?.quantity_enabled) {
			formData.append('quantity', data.quantity)
		}

		if (gates.supplierActive) {
			formData.append('supplier_barcode', data.supplier_barcode)
			formData.append('supplier_sku', data.supplier_sku)
		}
		formData.append('size', data.size)
		formData.append('category_id', data.category?.value ?? '')
		if (storeCtx?.storeSettings?.photos_required_to_publish && !hasImage) {
			formData.append('published', 'false')
		} else {
			formData.append('published', data.published)
		}
		formData.append('color', productColor)
		formData.append('product_condition_id', data.productCondition?.value ?? '')
		formData.append('product_fabric_id', data.productFabricType?.value ?? '')
		formData.append('product_gender_id', data.productGender?.value ?? '')
		formData.append('product_type_id', data.productType?.value ?? '')

		if (gates.vatActive && data.vat_rate?.value) {
			formData.append('vat_rate', data.vat_rate?.value)
		}

		if (formattedTags.length == 0) {
			formData.set('tags', '')
		}
		for (const tag of formattedTags) {
			formData.append('tags', tag)
		}

		if (!Array.isArray(image)) {
			formData.append('image', image, image.name)
		} else {
			formData.append('image', [] as unknown as Blob)
		}

		editProductMutation(formData)
	}

	const handleConfirm = () => {
		if (product?.data?.confirmed) {
			unconfirmProductMutation()
		} else {
			confirmProductMutation()
		}
	}

	return (
		<>
			<Header
				title={t('Edit product')}
				eyebrow={t('Products')}
				actions={
					<>
						<BackButton
							text={t('Back to products')}
							to={
								searchParams.toString()
									? `${routes.productIndex}?${searchParams.toString()}`
									: routes.productIndex
							}
						/>
						<HistoryButton to={routes.productHistory(params.id)} />
					</>
				}
			/>

			<div className="mx-auto max-w-[704px] space-y-5 px-5 lg:px-0">
				<Form
					variant="sections"
					submitText={t('Save changes')}
					cancelText={t('Discard changes')}
					onCancel={() => reset()}
					onSubmit={handleSubmit(onSubmit)}
					isLoading={isEditProductMutationLoading}
					actions={
						product?.data?.confirmed != null && (
							<Button
								styleType="outline"
								className={outlineButton}
								onClick={handleConfirm}
								isLoading={isConfirmProductMutationLoading || isUnconfirmProductMutationLoading}>
								{product?.data?.confirmed ? t('Unconfirm') : t('Confirm')}
							</Button>
						)
					}>
					<ProductFormSections
						form={form}
						options={options}
						gates={gates}
						productColor={productColor}
						onColorChange={handleProductColorChange}
						publishedAt={product?.data?.published_at}
					/>
				</Form>

				{!!product?.data?.id && (
					<ProductSalesTable sales={product.data.sales} product_id={product.data.id} />
				)}
			</div>
		</>
	)
}

export default ProductPage
